/** Typed calls for custom metrics and custom views. Shapes mirror apps/api/src/custom-routes.ts. */

import { apiCall, orgPath } from '../api.ts';
import type { Filter } from '../reports/reports-api.ts';

export type DataType = 'INTEGER' | 'FLOAT' | 'CURRENCY' | 'PERCENT';
export type Aggregation = 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'NONE';
export type RuleTest = 'EQUALS' | 'REGEXP' | 'LT' | 'GT' | 'LTE' | 'GTE';

export type FormulaToken =
  | { kind: 'metric'; source: string; metric: string; filters?: Filter[] }
  | { kind: 'column'; column: string }
  | { kind: 'op'; op: '+' | '-' | '*' | '/' | '(' | ')' }
  | { kind: 'number'; value: number };

export interface CustomMetric {
  id: string;
  business_unit_id: string | null;
  name: string;
  description: string | null;
  data_type: DataType;
  higher_is_better: boolean;
  formula: FormulaToken[];
  updated_at: string;
}

export interface ViewRule {
  test: RuleTest;
  value: string;
  result: string;
  source: string;
}

export interface ViewSourceRow {
  id: string;
  source: string;
  dimension: string;
  filters: Filter[];
}

export interface ViewColumn {
  id: string;
  name: string;
  kind: 'VALUE' | 'COMPUTED';
  data_type: DataType;
  aggregation: Aggregation;
  higher_is_better: boolean;
  metrics: Record<string, string>;
  formula: FormulaToken[] | null;
}

export interface ViewDefinition {
  dimension: { name: string; rules: ViewRule[] };
  sources: ViewSourceRow[];
  columns: ViewColumn[];
}

export interface CustomView {
  id: string;
  business_unit_id: string;
  name: string;
  definition: ViewDefinition;
  updated_at: string;
}

export const PLATFORM_DIMENSION = '__platform';
export const CUSTOM_SOURCE = 'custom';
export const VIEW_PREFIX = 'view:';

const path = (org: string, rest: string) => orgPath(org, `/reports${rest}`);
const json = (method: string, body: unknown) => ({ method, body: JSON.stringify(body) });

export const customApi = {
  metrics: (org: string, clientId: string | null) =>
    apiCall<{ metrics: CustomMetric[] }>(
      path(
        org,
        `/custom-metrics${clientId ? `?business_unit_id=${encodeURIComponent(clientId)}` : ''}`,
      ),
    ).then((data) => data.metrics),
  saveMetric: (org: string, input: Omit<CustomMetric, 'id' | 'updated_at'>, id?: string) =>
    apiCall<{ metric: CustomMetric }>(
      path(org, id ? `/custom-metrics/${id}` : '/custom-metrics'),
      json(id ? 'PUT' : 'POST', input),
    ).then((data) => data.metric),
  removeMetric: (org: string, id: string) =>
    apiCall<{ deleted: boolean }>(path(org, `/custom-metrics/${id}`), { method: 'DELETE' }),
  views: (org: string, clientId: string) =>
    apiCall<{ views: CustomView[] }>(
      path(org, `/custom-views?business_unit_id=${encodeURIComponent(clientId)}`),
    ).then((data) => data.views),
  saveView: (
    org: string,
    input: { business_unit_id: string; name: string; definition: ViewDefinition },
    id?: string,
  ) =>
    apiCall<{ view: CustomView }>(
      path(org, id ? `/custom-views/${id}` : '/custom-views'),
      json(id ? 'PUT' : 'POST', input),
    ).then((data) => data.view),
  removeView: (org: string, id: string) =>
    apiCall<{ deleted: boolean }>(path(org, `/custom-views/${id}`), { method: 'DELETE' }),
};
